import { Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslatePipe } from '@ngx-translate/core';
import { JjamesButtonComponent } from 'shared-lib';
import { ParkingSpaceService, ParkingEntryType } from '../../../api/gen';
import { ParkingSpaceDto } from '../../../api/gen/model/parkingSpaceDto';

export interface LatePaymentSettingsDialogData {
    parkingSpace: ParkingSpaceDto;
}

/**
 * Edits the four late-payment settings for one space (KAN-458 D-37).
 *
 * Until this existed the columns could only be changed with a hand-written UPDATE, which is why
 * enabling late payment at a site was a developer task.
 *
 * The dialog always submits all four values, never a partial patch: on the server a null ceiling
 * means *uncapped*, so a field left out would silently read as "remove the cap" rather than "leave
 * it alone". Blanking a field here is therefore a real, intended edit — that is the whole point of
 * the endpoint being a replace.
 */
@Component({
    selector: 'app-late-payment-settings-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        JjamesButtonComponent,
        TranslatePipe
    ],
    templateUrl: './late-payment-settings-dialog.component.html'
})
export class LatePaymentSettingsDialogComponent {
    private dialogRef = inject(MatDialogRef<LatePaymentSettingsDialogComponent>);
    private parkingSpaceService = inject(ParkingSpaceService);
    private snackBar = inject(MatSnackBar);
    readonly data = inject<LatePaymentSettingsDialogData>(MAT_DIALOG_DATA);

    enabled = signal(this.data.parkingSpace.latePaymentEnabled ?? false);
    windowHours = signal<number | null>(this.data.parkingSpace.latePaymentWindowHours ?? 48);
    maxAmount = signal<number | null>(this.data.parkingSpace.latePaymentMaxAmount ?? null);
    abandonedStayThresholdHours = signal<number | null>(
        this.data.parkingSpace.abandonedStayThresholdHours ?? null
    );

    isSaving = signal(false);

    /**
     * Gated sites hold the barrier until the parker pays, so they should not normally leave stays
     * unsettled for the sweep to pick up. Shown rather than blocked — a site can be mid-conversion
     * to free-flow, and the server allows it for the same reason.
     */
    readonly gatedWarning = computed(
        () => this.enabled() && this.data.parkingSpace.entryType === ParkingEntryType.Gated
    );

    /** A window is mandatory whenever the feature is on; the other two are genuinely optional. */
    readonly canSave = computed(() => {
        if (this.isSaving()) return false;
        if (!this.enabled()) return true;
        const hours = this.windowHours();
        return hours !== null && hours >= 1;
    });

    onCancel(): void {
        this.dialogRef.close(false);
    }

    onSave(): void {
        if (!this.canSave()) return;
        this.isSaving.set(true);

        this.parkingSpaceService
            .parkingSpaceParkingSpaceIdLatePaymentSettingsPut(this.data.parkingSpace.id!, {
                enabled: this.enabled(),
                windowHours: this.windowHours() ?? 48,
                // Empty means "no ceiling" / "not reported", which is exactly what null encodes
                // server-side — so an empty box must reach the API as null, not be dropped.
                maxAmount: this.maxAmount(),
                abandonedStayThresholdHours: this.abandonedStayThresholdHours()
            })
            .subscribe({
                next: () => {
                    this.isSaving.set(false);
                    this.dialogRef.close(true);
                },
                error: (err) => {
                    this.isSaving.set(false);
                    console.error('Failed to update late-payment settings', err);
                    this.snackBar.open(
                        err?.error?.detail ?? 'Could not save the late-payment settings.',
                        'Dismiss',
                        { duration: 5000 }
                    );
                }
            });
    }
}
